import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, resolveImageUrl } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Phone, Mail, Globe, MapPin, Download, Copy, Share2, Trophy
} from 'lucide-react';

// SVG social icons
const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
);
const TwitterIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
);
const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
);
const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/></svg>
);

const BusinessCardDisplay = ({ data, qrUrl, isPublic }) => {
  if (!data) return null;
  const { card } = data;

  const handleSaveContact = () => {
    if (!data.vcard_data && !qrUrl) return;
    // Create vCard download
    const vcard = data.vcard_data || `BEGIN:VCARD\nVERSION:3.0\nFN:${card.full_name}\nEND:VCARD`;
    const blob = new Blob([vcard], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${card.full_name?.replace(/\s+/g, '_') || 'contact'}.vcf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-md mx-auto" data-testid="business-card-display">
      {/* Card container - styled like a real business card */}
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
        {/* Dark header */}
        <div className="bg-gradient-to-br from-[#0f172a] to-[#1e3a5f] p-6">
          <div className="flex items-start gap-4">
            <div className="w-20 h-20 rounded-xl overflow-hidden border-2 border-white/20 flex-shrink-0 bg-white/10">
              {card.photo_url ? (
                <img src={resolveImageUrl(card.photo_url)} alt={card.full_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-3xl font-bold text-white/80">{card.full_name?.[0] || 'U'}</span>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-white truncate">{card.full_name}</h2>
              {card.is_founding_member && (
                <div className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-[10px] font-bold shadow-sm" data-testid="bcard-founding-badge">
                  <Trophy className="w-3 h-3" />
                  Founding Member{card.founding_member_number ? ` #${card.founding_member_number}` : ''}
                </div>
              )}
              <p className="text-sm text-blue-300 font-medium">{card.profession}</p>
              <p className="text-sm text-white/60">{card.company}</p>
            </div>
            {card.logo_url && (
              <img src={resolveImageUrl(card.logo_url)} alt="Logo" className="w-10 h-10 rounded-lg object-contain bg-white/10 p-1" />
            )}
          </div>
        </div>

        {/* Contact info on white background */}
        <div className="p-6 space-y-3">
          {card.phone && (
            <a href={`tel:${card.phone}`} className="flex items-center gap-3 text-gray-700 hover:text-primary transition-colors text-sm" data-testid="bcard-phone">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Phone className="w-4 h-4 text-blue-600" />
              </div>
              {card.phone}
            </a>
          )}
          {card.email && (
            <a href={`mailto:${card.email}`} className="flex items-center gap-3 text-gray-700 hover:text-primary transition-colors text-sm" data-testid="bcard-email">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4 text-blue-600" />
              </div>
              {card.email}
            </a>
          )}
          {card.website && (
            <a href={card.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-gray-700 hover:text-primary transition-colors text-sm" data-testid="bcard-website">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Globe className="w-4 h-4 text-blue-600" />
              </div>
              {card.website}
            </a>
          )}
          {(card.address?.street || card.address?.city || card.address?.state) && (
            <div className="flex items-start gap-3 text-gray-700 text-sm" data-testid="bcard-address">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <MapPin className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                {card.address?.street && <p>{card.address.street}</p>}
                <p>{[card.address?.city, card.address?.state, card.address?.zip].filter(Boolean).join(', ')}</p>
                {card.address?.country && card.address.country !== 'USA' && <p>{card.address.country}</p>}
              </div>
            </div>
          )}

          {/* Social links with real brand icons */}
          {(card.social?.linkedin || card.social?.twitter || card.social?.facebook || card.social?.instagram) && (
            <div className="flex gap-2 pt-2 border-t border-gray-100">
              {card.social?.linkedin && (
                <a href={card.social.linkedin} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-[#0077B5] rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity text-white">
                  <LinkedInIcon />
                </a>
              )}
              {card.social?.twitter && (
                <a href={card.social.twitter} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-black rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity text-white">
                  <TwitterIcon />
                </a>
              )}
              {card.social?.facebook && (
                <a href={card.social.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-[#1877F2] rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity text-white">
                  <FacebookIcon />
                </a>
              )}
              {card.social?.instagram && (
                <a href={card.social.instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-gradient-to-br from-[#f09433] via-[#e6683c] to-[#dc2743] rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity text-white">
                  <InstagramIcon />
                </a>
              )}
            </div>
          )}
        </div>

        {/* QR Code section */}
        {qrUrl && (
          <div className="px-6 pb-6">
            <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-4 border border-gray-100">
              <img src={qrUrl} alt="QR Code" className="w-20 h-20 rounded-lg" data-testid="bcard-qr" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Scan to Save Contact</p>
                <p className="text-xs text-gray-500 mt-1">Opens vCard in your phone's contacts</p>
              </div>
            </div>
          </div>
        )}

        {/* Save Contact Button */}
        <div className="px-6 pb-6">
          <Button
            onClick={handleSaveContact}
            className="w-full bg-gradient-to-r from-[#0f172a] to-[#1e3a5f] hover:opacity-90 text-white font-semibold py-3 h-auto"
            data-testid="save-contact-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Save Contact to Phone
          </Button>
        </div>
      </div>
    </div>
  );
};

// My Business Card page (authenticated)
export const MyBusinessCard = () => {
  const { user } = useAuth();
  const [cardData, setCardData] = useState(null);
  const [qrUrl, setQrUrl] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const data = await api.getBusinessCard();
        setCardData(data);
        setQrUrl(data.qr_code_url);
      } catch (error) {
        console.error('Failed to fetch business card:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCard();
  }, []);

  const handleCopyLink = () => {
    const url = `${window.location.origin}/card/${user?.id}`;
    navigator.clipboard.writeText(url);
    toast.success('Business card link copied!');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-2xl mx-auto" data-testid="my-business-card">
        <div className="text-center">
          <h1 className="text-3xl font-bold font-['Outfit'] tracking-tight">Your Digital Business Card</h1>
          <p className="text-muted-foreground mt-1">Share your QR code — anyone who scans it gets your full contact info</p>
        </div>

        <BusinessCardDisplay data={cardData} qrUrl={qrUrl} />

        <div className="flex justify-center gap-3">
          <Button onClick={handleCopyLink} variant="outline" data-testid="copy-card-link">
            <Copy className="w-4 h-4 mr-2" /> Copy Link
          </Button>
          <Button onClick={() => { if (navigator.share) navigator.share({ title: 'My Business Card', url: `${window.location.origin}/card/${user?.id}` }); else handleCopyLink(); }} data-testid="share-card">
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
        </div>

        <Card className="border-border/50">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-muted-foreground">
              This QR code contains your vCard data. When scanned, it opens your digital business card. Visitors can then tap "Save Contact to Phone" to add your info to their contacts.
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

// Public Business Card page (anyone can view via QR scan)
export const PublicBusinessCard = () => {
  const { userId } = useParams();
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState([]);
  const [showRecForm, setShowRecForm] = useState(false);
  const [recText, setRecText] = useState('');
  const [recRel, setRecRel] = useState('colleague');
  const [submittingRec, setSubmittingRec] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    // Guard against invalid /card/:userId values (e.g. routes built from
    // stale data where the id is missing or the literal string "undefined").
    // Without this guard the page renders as a near-blank screen with only
    // "Business card not found" — looks like a white screen to users.
    if (!userId || userId === 'undefined' || userId === 'null') {
      setLoading(false);
      return;
    }
    const fetchCard = async () => {
      try {
        const data = await api.getPublicBusinessCard(userId);
        setCardData(data);
        // Log profile view if logged in
        api.logProfileView(userId).catch(() => {});
        // Load recommendations
        const recData = await api.getRecommendations(userId);
        setRecommendations(recData.recommendations || []);
      } catch (error) {
        console.error('Failed to fetch business card:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCard();
  }, [userId]);

  const handleRecommend = async (e) => {
    e.preventDefault();
    if (!recText.trim()) return;
    setSubmittingRec(true);
    try {
      await api.addRecommendation(userId, recText, recRel);
      const recData = await api.getRecommendations(userId);
      setRecommendations(recData.recommendations || []);
      setShowRecForm(false);
      setRecText('');
      if (typeof toast !== 'undefined') toast.success('Recommendation added!');
    } catch (err) {
      if (typeof toast !== 'undefined') toast.error(err.response?.data?.detail || 'Failed to add recommendation');
    } finally {
      setSubmittingRec(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!cardData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-6">
        <div className="max-w-sm w-full text-center bg-white rounded-2xl shadow-sm border border-gray-100 p-6" data-testid="bcard-not-found">
          <div className="w-12 h-12 mx-auto rounded-full bg-amber-50 flex items-center justify-center mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-1 font-['Outfit']">Business card not found</h2>
          <p className="text-sm text-gray-500 mb-4">
            We couldn't find that business card. It may have been removed, or the link may be incomplete.
          </p>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => { window.location.href = user ? '/dashboard' : '/'; }}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
              data-testid="bcard-go-home"
            >
              {user ? 'Back to dashboard' : 'Go to ConnectMail'}
            </button>
            {user && (
              <button
                onClick={() => { window.location.href = '/network'; }}
                className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                data-testid="bcard-browse-network"
              >
                Browse network
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-12 px-4" data-testid="public-business-card">
      <BusinessCardDisplay data={cardData} isPublic />

      {/* Recommendations Section */}
      <div className="max-w-md mx-auto mt-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800">Recommendations ({recommendations.length})</h3>
            {user && user.id !== userId && (
              <button
                onClick={() => setShowRecForm(!showRecForm)}
                className="text-xs text-blue-600 hover:underline"
                data-testid="write-recommendation-btn"
              >
                {showRecForm ? 'Cancel' : 'Write a recommendation'}
              </button>
            )}
          </div>

          {showRecForm && (
            <form onSubmit={handleRecommend} className="mb-4 space-y-3">
              <select
                value={recRel}
                onChange={e => setRecRel(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              >
                <option value="colleague">Colleague</option>
                <option value="client">Client</option>
                <option value="partner">Business Partner</option>
                <option value="friend">Friend</option>
                <option value="mentor">Mentor/Mentee</option>
              </select>
              <textarea
                value={recText}
                onChange={e => setRecText(e.target.value)}
                placeholder="Write your recommendation here..."
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none"
                required
              />
              <button
                type="submit"
                disabled={submittingRec || !recText.trim()}
                className="w-full bg-blue-600 text-white rounded-lg py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                data-testid="submit-recommendation-btn"
              >
                {submittingRec ? 'Submitting...' : 'Submit Recommendation'}
              </button>
            </form>
          )}

          {recommendations.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-3">No recommendations yet</p>
          ) : (
            <div className="space-y-3">
              {recommendations.map(rec => (
                <div key={rec.id} className="flex gap-3" data-testid={`rec-${rec.id}`}>
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-bold flex-shrink-0 overflow-hidden">
                    {rec.recommender_photo ? (
                      <img src={resolveImageUrl(rec.recommender_photo)} alt="" className="w-full h-full object-cover" />
                    ) : rec.recommender_name?.[0]}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-700">{rec.recommender_name} <span className="text-gray-400 font-normal">· {rec.relationship}</span></p>
                    <p className="text-xs text-gray-500">{rec.recommender_profession}</p>
                    <p className="text-sm text-gray-700 mt-1 italic">"{rec.text}"</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="text-center mt-6">
        <a href="/" className="text-xs text-gray-400 hover:text-gray-500 transition-colors">Powered by ConnectMail</a>
      </div>
    </div>
  );
};

export default MyBusinessCard;
